import AddToBasketOverlay from "./add-to-basket-overlay";
import fastdom from "fastdom";
import Helpers from "./helpers";
import AlgoliaTracking from "./algolia-tracking";
import Logger from "./logger";
import { VariantSelectorElement } from "../../ui/components/product-variant-selector";
import {IViewItemGA4Item} from "../../ui/models/IViewItemGA4Item";

declare var $: any;
declare var popups: any;
declare var SiteStore: any;
declare var dataLayer: any;

export default class Ecommerce {

	private currentElement: HTMLElement;

	private ajaxUrl = '/api/basketadd/addajax/';

	private disabledClass = 'disabled';

	private addingMessage = 'Adding...';

	private errorTitle = 'Error';

	private errorMessage = 'Sorry, we could not add the product to your basket. Please try again.';

	private buttonText: String;

	private addToBasketOverlay = new AddToBasketOverlay();

	constructor(currentElement: HTMLElement) {

		this.currentElement = currentElement;
		this.setup();

	}

	private setup() {

		if (this.currentElement.hasAttribute('data-atb-button-enable')) {
			this.currentElement.removeAttribute('disabled');
		}

		this.currentElement.addEventListener('click', (e) => this.handleButtonClick(e));

	}

	private handleButtonClick(e: Event) {

		const context = this;
		const isFormValid = this.validateForm(e.target);

		if (!isFormValid) {
			// on product pages the selects are hidden, so need to remove focus to prevent focus trap on iOS
			if (matchMedia('(min-width: 980px)').matches) {
				setTimeout(function () {
					const currentActiveElement = document.activeElement as HTMLInputElement;
					currentActiveElement.blur();
				}, 500);
			}
			e.preventDefault();
			return;
		}

		this.disableButton(e.target);

		this.submitForm(e.target).then(function (response) {

			if (response.Success) {

				context.trackAddToBasket(response.AddedProducts);
				Ecommerce.updateBasketCounts(response.BasketTotalItemQuantity);

				popups.allow = true;
				popups.close();

				fastdom.mutate(() => {
					context.addToBasketOverlay.open(response);
				});

			} else {

				context.handleError(new Error(`Add to basket failed. User message: ${response.Message}`));

			}

		}).
			catch((error) => {
				Logger.trackException(error, 'Unknown error in add to basket');
			});

		e.preventDefault();

	}

	private validateForm(button: any) {

		const form = button.closest('form');

		const variantSelector = form.querySelector('product-variant-selector') as VariantSelectorElement;
		if(variantSelector) {
			if (!variantSelector.isValid()) {
				return false;
			}
		}

		const $form = $(form);
		return $form.valid();

	}

	private disableButton(button: any) {

		this.buttonText = button.innerHTML;

		button.classList.add(this.disabledClass);
		button.setAttribute('disabled', 'disabled');
		button.textContent = this.addingMessage;

	}

	private enableButton(button: any) {

		button.classList.remove(this.disabledClass);
		button.removeAttribute('disabled');
		button.innerHTML = this.buttonText;

	}

	private submitForm(button: any) {

		const context = this;
		const form = button.closest('form');
		let serializedData = Helpers.serialize(form) as any;
		if (AlgoliaTracking.queryID()) {
			serializedData += '&queryId=' + AlgoliaTracking.queryID();

		}

		Logger.trackTrace(`Add to basket called with: ${serializedData}`);

		return fetch(this.ajaxUrl, {
			method: 'POST',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
				'Cache-Control': 'no-cache'
			},
			body: serializedData
		}).then(function (response: Response) {

			context.enableButton(button);

			if (response.ok) {
				return response.json();
			} else { throw new Error(`Add to basket endpoint errored with statuscode ${response.text.toString() }`); }

		});

	}

	private handleError(error?: Error) {

		popups.loader(true);
		popups.allow = true;
		popups.close();
		popups.fadeClick = true;
		popups.confirm(this.errorTitle, this.errorMessage, '', true);

		if (error) {
			Logger.trackException(error);
		}
	}

	private trackAddToBasket(addedProducts: any) {
		Helpers.deferTask(() => {

			let value: number = 0;
			let gaData: IViewItemGA4Item[] = [];
			let algoliaProductIds: string[] = [];
			let algoliaProductData: {
				price: number,
				discount?: number,
				quantity: number
			}[] = [];
			let productNames: string[] = [];

			for (let i = 0; i < addedProducts.length; i++) {
				const currentProduct = addedProducts[i];
				let viewItemEventInfo = JSON.parse(currentProduct.ViewItemEventInfoJson) as IViewItemGA4Item;
				viewItemEventInfo.quantity = currentProduct.Quantity;
				value += currentProduct.Price.Price * currentProduct.Quantity;
				gaData.push(viewItemEventInfo);
				algoliaProductIds.push(currentProduct.Guid);
				algoliaProductData.push({
					price: currentProduct.Price.Price,
					quantity: currentProduct.Quantity
				});
				productNames.push(currentProduct.DisplayName);
			}

			value = parseFloat(value.toFixed(2));

			dataLayer.push({ ecommerce: null });
			dataLayer.push({
				event: 'add_to_cart',
				ecommerce: {
					value: value,
					items: gaData
				}
			});

			AlgoliaTracking.addedToCart(algoliaProductIds, algoliaProductData, value);

			Logger.trackTrace(`Add to basket success: ${productNames.join(', ')} added to basket`);

		});
	}

	static updateBasketCounts(quantity: any) {

		Helpers.deferTask(() => {
			if (!quantity && quantity !== 0) {
				return;
			}

			const basketCount = document.querySelector('#navBasketCount') as HTMLElement;
			if (basketCount) {
				basketCount.textContent = quantity;
			}

			const mobileBasketCount = document.getElementById('navMobileBasketCount');
			if (mobileBasketCount) {
				mobileBasketCount.textContent = quantity;
			}

			const basketTotalLink = document.querySelector('.js-basket-total-link');
			basketTotalLink.setAttribute('aria-label', `${quantity} item${parseInt(quantity) !== 1 ? 's' : ''} in shopping basket`);

			const basketLink = document.querySelector('#navBasketLink') as HTMLElement;
			basketLink.classList.add('c-header__utils__item--basket-populated');

		});

	}

}
